/**
 * Age Range Utilities for Recipe Filtering
 * Handles both month-based (6-12) and year-based (0-1) age ranges
 */

export interface AgeRangeNormalized {
  minYears: number;
  maxYears: number;
  original: string;
}

/**
 * Normalize an age range string to years
 * Handles formats like:
 * - "6-9" (could be months or years)
 * - "6-12" (months - max 24 months before treating as years)
 * - "12-18" (months)
 * - "0-1" (years)
 * - "3-4" (years)
 * 
 * @param ageRange - Age range string like "6-9", "0-1", "12-18"
 * @returns Normalized age range in years
 */
export function normalizeAgeRange(ageRange: string): AgeRangeNormalized | null {
  if (!ageRange || typeof ageRange !== 'string') {
    return null;
  }

  // Parse the range
  const parts = ageRange.split('-').map(s => s.trim());
  if (parts.length !== 2) {
    return null;
  }

  const start = parseInt(parts[0]);
  const end = parseInt(parts[1]);

  if (isNaN(start) || isNaN(end) || start < 0 || end < start) {
    return null;
  }

  // IMPROVED LOGIC: More precise classification of months vs years
  // Based on actual recipe data patterns and baby food age ranges
  
  let minYears: number;
  let maxYears: number;
  const span = end - start;

  // RULE 1: If max age <= 2, it's ALWAYS years (0-1, 1-2, 0-2)
  if (end <= 2) {
    minYears = start;
    maxYears = end;
  }
  // RULE 2: If starts at 0 and ends <= 12, it's MONTHS (0-3, 0-5, 0-6, 0-12)
  else if (start === 0 && end <= 12) {
    minYears = 0;
    maxYears = end / 12;
  }
  // RULE 3: If both are in 6-24 range with small span, it's MONTHS (6-8, 6-9, 6-12, 9-12, 12-18, 18-24)
  else if (start >= 6 && end <= 24 && span <= 12) {
    minYears = start / 12;
    maxYears = end / 12;
  }
  // RULE 4: If starts at 3+ and span <= 4, it's YEARS (3-4, 5-6, 7-8, 9-10, 11-12)
  else if (start >= 3 && span <= 4) {
    minYears = start;
    maxYears = end;
  }
  // RULE 5: Edge case - 2-3 could be years (toddler transition)
  else if (start === 2 && end === 3) {
    minYears = start;
    maxYears = end;
  }
  // DEFAULT: Treat as years for safety
  else {
    minYears = start;
    maxYears = end;
  }

  return {
    minYears,
    maxYears,
    original: ageRange,
  };
}

/**
 * Check if two age ranges overlap
 * 
 * @param range1 - First age range (e.g., "0-1" from filter)
 * @param range2 - Second age range (e.g., "6-12" from recipe)
 * @returns true if ranges overlap
 * 
 * @example
 * ageRangesOverlap("0-1", "6-12") // true (6-12 months = 0.5-1 years, overlaps with 0-1 years)
 * ageRangesOverlap("3-4", "6-12") // false (no overlap)
 */
export function ageRangesOverlap(range1: string, range2: string): boolean {
  const norm1 = normalizeAgeRange(range1);
  const norm2 = normalizeAgeRange(range2);

  if (!norm1 || !norm2) {
    console.warn(`[ageRangesOverlap] Invalid range: ${range1} or ${range2}`);
    return false;
  }

  // Check for overlap: [min1, max1] overlaps [min2, max2]
  // Use STRICT inequalities to avoid FALSE POSITIVES at boundaries
  // Two ranges overlap if: min1 < max2 AND max1 > min2
  // This ensures ranges that just "touch" at boundaries don't count as overlapping
  const overlaps = norm1.minYears < norm2.maxYears && norm1.maxYears > norm2.minYears;
  
  // Debug logging for development
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log(
      `[ageRangesOverlap] "${range1}" (${norm1.minYears.toFixed(2)}-${norm1.maxYears.toFixed(2)} years) ` +
      `vs "${range2}" (${norm2.minYears.toFixed(2)}-${norm2.maxYears.toFixed(2)} years) = ${overlaps ? 'OVERLAP' : 'NO OVERLAP'}`
    );
  }

  return overlaps;
}

/**
 * Get all unique filter options from a list of age ranges
 * Consolidates both month-based and year-based ranges into year-based options
 * 
 * SIMPLIFIED APPROACH: Only show filter if recipes actually fall in that range
 * 
 * @param ageRanges - Array of age range strings from recipes
 * @returns Sorted array of year-based filter options
 * 
 * @example
 * getAgeFilterOptions(["6-9", "6-12", "12-18", "0-1", "3-4", "5-6"])
 * // Returns: ["0-1", "1-2", "3-4", "5-6"]
 */
export function getAgeFilterOptions(ageRanges: string[]): string[] {
  const filterSet = new Set<string>();

  // Normalize all recipe ranges and bucket them using OVERLAP logic
  // Each recipe can show in ALL filters it overlaps with
  for (const ageRange of ageRanges) {
    const normalized = normalizeAgeRange(ageRange);
    if (!normalized) continue;

    const { minYears, maxYears } = normalized;

    // For each standard filter range, check if recipe overlaps
    // Overlap = (rangeMin < recipeMax) AND (rangeMax > recipeMin)
    
    // 0-1 years filter: [0, 1]
    if (0 < maxYears && 1 > minYears) {
      filterSet.add('0-1');
    }
    
    // 1-2 years filter: [1, 2]
    if (1 < maxYears && 2 > minYears) {
      filterSet.add('1-2');
    }
    
    // 3-4 years filter: [3, 4]
    if (3 < maxYears && 4 > minYears) {
      filterSet.add('3-4');
    }
    
    // 5-6 years filter: [5, 6]
    if (5 < maxYears && 6 > minYears) {
      filterSet.add('5-6');
    }
    
    // 7-8 years filter: [7, 8]
    if (7 < maxYears && 8 > minYears) {
      filterSet.add('7-8');
    }
    
    // 9-10 years filter: [9, 10]
    if (9 < maxYears && 10 > minYears) {
      filterSet.add('9-10');
    }
    
    // 11-12 years filter: [11, 12]
    if (11 < maxYears && 12 > minYears) {
      filterSet.add('11-12');
    }
  }

  // Sort ranges by starting age
  return Array.from(filterSet).sort((a, b) => {
    const aStart = parseInt(a.split('-')[0]);
    const bStart = parseInt(b.split('-')[0]);
    return aStart - bStart;
  });
}

/**
 * Filter recipes by age range with overlap matching
 * 
 * @param recipes - Array of recipes with ageRange property
 * @param filterAgeRange - Selected filter age range (e.g., "0-1")
 * @returns Filtered recipes that overlap with the selected age range
 */
export function filterRecipesByAgeRange<T extends { ageRange?: string | null }>(
  recipes: T[],
  filterAgeRange: string | undefined
): T[] {
  if (!filterAgeRange) {
    return recipes; // No filter applied, return all
  }

  return recipes.filter(recipe => {
    if (!recipe.ageRange) {
      return false; // Exclude recipes without age range
    }

    return ageRangesOverlap(filterAgeRange, recipe.ageRange);
  });
}

/**
 * Debug helper to show age range conversion
 * Useful for testing and validation
 */
export function debugAgeRange(ageRange: string): string {
  const normalized = normalizeAgeRange(ageRange);
  if (!normalized) {
    return `❌ Invalid: "${ageRange}"`;
  }

  const yearsDisplay = normalized.minYears === normalized.maxYears
    ? `${normalized.minYears} year${normalized.minYears !== 1 ? 's' : ''}`
    : `${normalized.minYears}-${normalized.maxYears} years`;

  return `"${ageRange}" → ${yearsDisplay} (${normalized.minYears.toFixed(2)}-${normalized.maxYears.toFixed(2)} years)`;
}

